import { clamp, mapLinear, MulticastDelegate, MulticastDelegate2 } from "flavr-common";
import { AudioListener } from "three";
const indicatorThreshold = 0.2;
const indicatorHistory = 3;
export function createAudioLevel(stream, onLevelChanged) {
    try {
        const audioIndicator = new AudioIndicator(stream, indicatorThreshold, 100, indicatorHistory, 0, -70);
        audioIndicator.onVolumeChanged.add(onLevelChanged);
        return audioIndicator;
    }
    catch (error) {
        console.error("[audio] createAudioLevel: error creating audio indicator", error);
        return undefined;
    }
}
export function createAudioIndicator(stream, onSpeak, onStopSpeak) {
    try {
        const audioIndicator = new AudioIndicator(stream, indicatorThreshold, 100, indicatorHistory);
        audioIndicator.onSpeaking.add(onSpeak);
        audioIndicator.onStoppedSpeaking.add(onStopSpeak);
        return audioIndicator;
    }
    catch (error) {
        return undefined;
    }
}
export function stopAudioIndicator(audioIndicator) {
    audioIndicator.onSpeaking.clear();
    audioIndicator.onStoppedSpeaking.clear();
    audioIndicator.onVolumeChanged.clear();
    audioIndicator.stop();
}
export class AudioIndicator {
    constructor(stream, thresholdPercent, interval = 50, history = 10, maxVolume = 100, minVolume = -100, smoothing = 0.1, play) {
        this.stream = stream;
        this.thresholdPercent = thresholdPercent;
        this.interval = interval;
        this.history = history;
        this._isSpeaking = false;
        this.isRunnning = true;
        this.speakingHistory = [];
        this.lastVolume = 0;
        this.threshold = -50;
        this._maxVolume = 100;
        this._minVolume = -100;
        this._currentVolume = Infinity;
        this.onVolumeChanged = new MulticastDelegate2();
        this.onSpeaking = new MulticastDelegate();
        this.onStoppedSpeaking = new MulticastDelegate();
        this.audioListener = new AudioListener();
        this.loop = () => {
            if (!this.isRunnning)
                return;
            const currentVolume = this.getMaxVolume();
            let history = 0;
            if (!this._isSpeaking && currentVolume >= this.threshold) {
                for (let i = this.speakingHistory.length - 3; i < this.speakingHistory.length; i++) {
                    history += this.speakingHistory[i];
                }
                if (history >= 2) {
                    this._isSpeaking = true;
                    this.onSpeaking.invoke();
                }
            }
            if (currentVolume !== this.lastVolume) {
                this.lastVolume = currentVolume;
                this.onVolumeChanged.invoke(currentVolume, this.threshold);
            }
            if (this._isSpeaking && currentVolume < this.threshold) {
                for (let i = 0; i < this.speakingHistory.length; i++) {
                    history += this.speakingHistory[i];
                }
                if (history === 0) {
                    this._isSpeaking = false;
                    this.onStoppedSpeaking.invoke();
                }
            }
            this.speakingHistory.shift();
            this.speakingHistory.push(0 + (currentVolume > this.threshold ? 1 : 0));
            this.startLoop();
        };
        this._maxVolume = maxVolume;
        this._minVolume = minVolume;
        this.analyser = this.audioListener.context.createAnalyser();
        this.analyser.fftSize = 512;
        this.analyser.smoothingTimeConstant = smoothing;
        this.fftBins = new Float32Array(this.analyser.frequencyBinCount);
        if (stream instanceof HTMLAudioElement || stream instanceof HTMLVideoElement) {
            // Audio Tag
            this.sourceNode = this.audioListener.context.createMediaElementSource(stream);
            if (typeof play === "undefined")
                play = true;
            this.thresholdPercent = thresholdPercent || 0.2;
        }
        else {
            // WebRTC Stream
            if (stream.getAudioTracks().length === 0) {
                throw new Error("No audio track found in stream");
            }
            this.sourceNode = this.audioListener.context.createMediaStreamSource(stream);
            this.thresholdPercent = thresholdPercent || 0.2;
        }
        for (let i = 0; i < this.history; ++i) {
            this.speakingHistory.push(0);
        }
        this.sourceNode.connect(this.analyser);
        if (play)
            this.analyser.connect(this.audioListener.context.destination);
        this.startLoop();
    }
    get maxVolume() {
        return this._maxVolume;
    }
    get minVolume() {
        return this._minVolume;
    }
    get volume() {
        return this._currentVolume;
    }
    stop() {
        this.isRunnning = false;
        this.stopLoop();
        this.onVolumeChanged.invoke(-100, this.threshold);
        if (this._isSpeaking) {
            this._isSpeaking = false;
            this.onStoppedSpeaking.invoke();
        }
        this.analyser.disconnect();
        this.sourceNode.disconnect();
        this.onVolumeChanged.clear();
        this.onSpeaking.clear();
        this.onStoppedSpeaking.clear();
    }
    startLoop() {
        this.stopLoop();
        this.loopHandler = self.setTimeout(this.loop, this.interval);
    }
    stopLoop() {
        if (this.loopHandler === undefined)
            return;
        clearTimeout(this.loopHandler);
        this.loopHandler = undefined;
    }
    getMaxVolume() {
        let maxVolume = -Infinity;
        this.analyser.getFloatFrequencyData(this.fftBins);
        for (let i = 4, ii = this.fftBins.length; i < ii; i++) {
            if (this.fftBins[i] > maxVolume && this.fftBins[i] < 0) {
                maxVolume = this.fftBins[i];
            }
        }
        this._currentVolume = maxVolume;
        if (maxVolume !== Infinity && maxVolume > this._maxVolume) {
            this._maxVolume = clamp(maxVolume, -100, 100);
        }
        if (maxVolume !== -Infinity && maxVolume < this._minVolume) {
            this._minVolume = clamp(maxVolume, -100, 100);
        }
        this.threshold = mapLinear(clamp(this.thresholdPercent, 0, 1), 0, 1, this.minVolume, this.maxVolume);
        return maxVolume;
    }
}
